import { type PerMessageReviewConfig } from "@/human-feedback/per-message"

import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"

const WORDS_PER_MESSAGE_STAT_NAME = "words_per_message"

interface WordsPerMessageStat {
  values?: {
    total_words?: number
    total_messages?: number
    words_per_message?: number
  }
}

export class WordsPerMessageMetricRecord extends NumericalMetricRecord {
  documentation =
    "Measures the average number of words per message in a conversation."
  overviewSize = "small" as const

  getHumanReviewDisplayValue(review: {
    output_type: string
    float_value: number | null
    string_value: string | null
    set_value: string[] | null
    json_value: Record<string, unknown> | null
  }): string | null {
    // Per-message review stores average in json_value
    if (
      review.json_value &&
      typeof review.json_value.average_words_per_message === "number" &&
      Number.isFinite(review.json_value.average_words_per_message)
    ) {
      return review.json_value.average_words_per_message.toFixed(2)
    }
    // Fall back to float_value (set by computeFloatValue)
    if (review.float_value != null && Number.isFinite(review.float_value)) {
      return review.float_value.toFixed(2)
    }
    return null
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const stat = getStat<WordsPerMessageStat>(
      mo,
      "custom",
      WORDS_PER_MESSAGE_STAT_NAME,
    )
    if (!stat) {
      return null
    }
    const { values } = stat
    if (!values) {
      return <div className="text-gray-500">No data available</div>
    }
    const { total_words, total_messages, words_per_message } = values
    if (
      typeof words_per_message !== "number" ||
      !Number.isFinite(words_per_message) ||
      typeof total_words !== "number" ||
      !Number.isFinite(total_words) ||
      typeof total_messages !== "number" ||
      !Number.isFinite(total_messages)
    ) {
      return <div className="text-gray-500">No data available</div>
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium mb-1">
              Words Per Message
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {words_per_message.toFixed(2)}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium mb-1">
              Total Words
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {total_words.toFixed(0)}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium mb-1">
              Total Messages
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {total_messages.toFixed(0)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return [
      { key: "0-9", label: "Word count" },
      { key: "Enter", label: "Next message" },
    ]
  }

  getPerMessageReviewConfig(): PerMessageReviewConfig {
    return {
      inputType: "number",
      inputLabel: "Word count",
      inputPlaceholder: "#",
      targetRoles: ["assistant"],
      buildGroundTruthJson({
        values,
        reviewedCount,
        totalTargetMessages,
      }: {
        values: Record<number, string | number | null>
        reviewedCount: number
        totalTargetMessages: number
      }) {
        const reviewedEntries = Object.entries(values).filter(
          ([, v]) => v !== null && v !== undefined,
        )
        const totalWords = reviewedEntries.reduce(
          (sum, [, v]) => sum + (v as number),
          0,
        )
        const avgWpm =
          reviewedEntries.length > 0
            ? totalWords / reviewedEntries.length
            : null
        return {
          word_counts_by_message_index: Object.fromEntries(reviewedEntries),
          total_words: totalWords,
          total_messages_reviewed: reviewedCount,
          total_assistant_messages: totalTargetMessages,
          average_words_per_message: avgWpm,
        }
      },
      extractValues(json: Record<string, unknown>) {
        const saved = json.word_counts_by_message_index as
          | Record<string, number>
          | undefined
        if (!saved) {
          return {}
        }
        const result: Record<number, number | null> = {}
        for (const [key, value] of Object.entries(saved)) {
          result[Number(key)] = value
        }
        return result
      },
      computeFloatValue({
        values,
      }: {
        values: Record<number, string | number | null>
        reviewedCount: number
        totalTargetMessages: number
      }) {
        const numericValues = Object.values(values).filter(
          (v): v is number => typeof v === "number",
        )
        if (numericValues.length === 0) {
          return null
        }
        return (
          numericValues.reduce((sum, v) => sum + v, 0) / numericValues.length
        )
      },
    }
  }
}
